import React, { useCallback, useEffect, useId, useRef, useState } from "react";
import { registerAppDialogHost } from "../lib/appDialog";
import { useBodyScrollLock } from "../lib/useBodyScrollLock";

const EMPTY = {
  kind: null,
  title: "",
  message: "",
  label: "",
  defaultValue: "",
  placeholder: "",
  inputType: "text",
  options: null,
  confirmLabel: "OK",
  cancelLabel: "Cancel",
  required: false,
  minLength: 0,
  tone: "default",
};

export default function AppDialogHost() {
  const [state, setState] = useState(EMPTY);
  const [value, setValue] = useState("");
  const [error, setError] = useState("");
  const resolverRef = useRef(null);
  const inputRef = useRef(null);
  const titleId = useId();
  const descId = useId();
  const open = Boolean(state.kind);
  useBodyScrollLock(open);

  const closeWith = useCallback((result) => {
    const resolve = resolverRef.current;
    resolverRef.current = null;
    setState(EMPTY);
    setValue("");
    setError("");
    if (resolve) resolve(result);
  }, []);

  useEffect(() => {
    return registerAppDialogHost((config) => {
      return new Promise((resolve) => {
        // If a dialog is already open, cancel it first.
        if (resolverRef.current) {
          resolverRef.current(config.kind === "confirm" ? false : config.kind === "prompt" ? null : undefined);
        }
        resolverRef.current = resolve;
        setError("");
        setValue(config.defaultValue ?? (config.options?.[0] ?? ""));
        setState({
          kind: config.kind,
          title: config.title || "",
          message: config.message || "",
          label: config.label || "",
          defaultValue: config.defaultValue ?? "",
          placeholder: config.placeholder || "",
          inputType: config.inputType || "text",
          options: config.options || null,
          confirmLabel: config.confirmLabel || "OK",
          cancelLabel: config.cancelLabel || "Cancel",
          required: Boolean(config.required),
          minLength: Number(config.minLength) || 0,
          tone: config.tone === "danger" ? "danger" : "default",
        });
      });
    });
  }, []);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (event) => {
      if (event.key === "Escape") {
        event.preventDefault();
        if (state.kind === "alert") closeWith(undefined);
        else if (state.kind === "confirm") closeWith(false);
        else closeWith(null);
      }
    };
    window.addEventListener("keydown", onKey);
    const focusTimer = window.setTimeout(() => {
      inputRef.current?.focus?.();
      if (typeof inputRef.current?.select === "function" && state.kind === "prompt" && !state.options) {
        inputRef.current.select();
      }
    }, 40);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.clearTimeout(focusTimer);
    };
  }, [open, state.kind, state.options, closeWith]);

  if (!open) return null;

  const isDanger = state.tone === "danger";
  const confirmClass = isDanger
    ? "px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-aus-coral hover:opacity-90 transition active:scale-[0.98]"
    : "px-4 py-2.5 rounded-xl text-sm font-semibold text-white bg-aus-cobalt hover:bg-aus-cobalt/90 transition active:scale-[0.98]";

  const onConfirm = () => {
    if (state.kind === "alert") {
      closeWith(undefined);
      return;
    }
    if (state.kind === "confirm") {
      closeWith(true);
      return;
    }
    const next = String(value ?? "");
    if (state.required && !next.trim()) {
      setError("This field is required.");
      return;
    }
    if (state.minLength > 0 && next.trim().length < state.minLength) {
      setError(`Please enter at least ${state.minLength} characters.`);
      return;
    }
    if (state.inputType === "number" && next.trim() !== "" && !Number.isFinite(Number(next))) {
      setError("Enter a valid number.");
      return;
    }
    closeWith(next);
  };

  const onCancel = () => {
    if (state.kind === "alert") closeWith(undefined);
    else if (state.kind === "confirm") closeWith(false);
    else closeWith(null);
  };

  return (
    <div
      className="fixed inset-0 z-[10000] flex items-center justify-center p-4"
      role="presentation"
    >
      <button
        type="button"
        className="absolute inset-0 bg-aus-onyx/45 backdrop-blur-[2px]"
        aria-label="Dismiss dialog"
        onClick={onCancel}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby={titleId}
        aria-describedby={state.message ? descId : undefined}
        className="relative w-full max-w-md rounded-2xl border border-aus-onyx/10 bg-white shadow-2xl p-6"
      >
        <h2 id={titleId} className="font-heading text-xl font-bold text-aus-onyx text-pretty">
          {state.title}
        </h2>
        {state.message ? (
          <p id={descId} className="mt-2 text-sm text-aus-onyx/70 leading-relaxed whitespace-pre-line text-pretty">
            {state.message}
          </p>
        ) : null}

        {state.kind === "prompt" ? (
          <div className="mt-4 space-y-1.5">
            {state.label ? (
              <label className="block text-xs font-semibold text-aus-onyx/70" htmlFor="app-dialog-input">
                {state.label}
              </label>
            ) : null}
            {state.options ? (
              <select
                id="app-dialog-input"
                ref={inputRef}
                className="w-full glass-input px-3 py-2.5 text-sm"
                value={value}
                onChange={(e) => {
                  setValue(e.target.value);
                  setError("");
                }}
              >
                {state.options.map((opt) => (
                  <option key={opt} value={opt}>
                    {opt}
                  </option>
                ))}
              </select>
            ) : state.inputType === "textarea" ? (
              <textarea
                id="app-dialog-input"
                ref={inputRef}
                rows={4}
                className="w-full glass-input px-3 py-2.5 text-sm resize-y min-h-[96px]"
                value={value}
                placeholder={state.placeholder}
                onChange={(e) => {
                  setValue(e.target.value);
                  setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) {
                    e.preventDefault();
                    onConfirm();
                  }
                }}
              />
            ) : (
              <input
                id="app-dialog-input"
                ref={inputRef}
                type={state.inputType}
                className="w-full glass-input px-3 py-2.5 text-sm"
                value={value}
                placeholder={state.placeholder}
                onChange={(e) => {
                  setValue(e.target.value);
                  setError("");
                }}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    onConfirm();
                  }
                }}
              />
            )}
            {error ? <p className="text-xs text-aus-coral font-medium">{error}</p> : null}
          </div>
        ) : null}

        <div className="mt-5 flex items-center justify-end gap-2">
          {state.kind !== "alert" ? (
            <button
              type="button"
              onClick={onCancel}
              className="px-4 py-2.5 rounded-xl text-sm font-semibold text-aus-onyx border border-aus-onyx/15 hover:bg-aus-onyx/5 transition active:scale-[0.98]"
            >
              {state.cancelLabel}
            </button>
          ) : null}
          <button type="button" onClick={onConfirm} className={confirmClass} autoFocus={state.kind !== "prompt"}>
            {state.confirmLabel}
          </button>
        </div>
      </div>
    </div>
  );
}
